import type { ProfileData } from "@recursive-robot/react-jsx-parser";

/**
 * All profiling batches produced by a single React render pass share the same `cycleId`.
 * A cycle is assembled incrementally: the synchronous `render` batch arrives first, then any
 * lazily-invoked `callback` batches, then (if React-render profiling is on) the `react` batch
 * on a post-commit microtask.  Never treat a cycle as "complete" on first sight.
 */
export type CycleEntry = {
    cycleId: string;
    // The enclosing parser's cycleId; null for the page-root Canvas parser. Used to correlate the
    // Canvas + nested Template renders of one pass into a group (see `groupId`).
    parentCycleId: string | null;
    // The resolved root cycleId (topmost ancestor via `parentCycleId`); identifies the group.
    groupId: string;
    fileName?: string;
    // `performance.now()`-based clock value when the cycle's first batch began.
    startTime: number;
    render?: ProfileData;
    callbacks: ProfileData[];
    react?: ProfileData;
};

export type ProfilingState = {
    // Presence of these flags gates the parser's `onProfile` / `profileReactRender` props.
    enabled: boolean;
    reactRenderEnabled: boolean;
    // Root cycleIds in arrival order (oldest -> newest); acts as the group ring-buffer index.
    groupOrder: string[];
    // Root cycleId -> its member cycleIds (arrival order within the group).
    groupCycleIds: Record<string, string[]>;
    cyclesById: Record<string, CycleEntry>;
    selectedCycleId: string | null;
    // Retained-group cap (ring buffer); oldest groups (with all their cycles) are evicted beyond this.
    maxGroups: number;
};

export const PROFILING_SET_ENABLED = "PROFILING_SET_ENABLED";
export const PROFILING_SET_REACT_RENDER_ENABLED = "PROFILING_SET_REACT_RENDER_ENABLED";
export const PROFILING_BATCH_RECEIVED = "PROFILING_BATCH_RECEIVED";
export const PROFILING_SELECT_CYCLE = "PROFILING_SELECT_CYCLE";
export const PROFILING_CLEAR = "PROFILING_CLEAR";

export interface SetProfilingEnabledAction {
    type: typeof PROFILING_SET_ENABLED;
    enabled: boolean;
}

export interface SetReactRenderEnabledAction {
    type: typeof PROFILING_SET_REACT_RENDER_ENABLED;
    enabled: boolean;
}

export interface ProfilingBatchReceivedAction {
    type: typeof PROFILING_BATCH_RECEIVED;
    batch: ProfileData;
}

export interface SelectCycleAction {
    type: typeof PROFILING_SELECT_CYCLE;
    cycleId: string | null;
}

export interface ClearProfilingAction {
    type: typeof PROFILING_CLEAR;
}

export type ProfilingActionTypes =
    | SetProfilingEnabledAction
    | SetReactRenderEnabledAction
    | ProfilingBatchReceivedAction
    | SelectCycleAction
    | ClearProfilingAction;
